<template lang="html">
  <div ref="timelineMain" class="timeline-main unselectable">
    <div :style="timelineWidthSliderStyle" class="timeline-width-slider">
      <vue-slider
        v-model="timelineWidth"
        :min="componentWidth"
        :max="32000"
        :interval="1"
        :lazy="false"
        :height="8"
        :dot-size="12"
        :adsorb="true"
      />
    </div>
    <!-- Slides -->
    <div ref="timeline" :style="timelineStyle" class="timeline">
      <!-- Slides -->
      <div
        v-for="(slide, idx) in show.data.slideArray"
        :key="slide.id"
        v-dragged="e => onSlideDragged(e, slide, idx)"
        :style="slideStyle(slide, idx)"
        :class="{
          'slides-selected': groupedSlideIds.includes(slide.id)
        }"
        class="slides"
      >
        <div v-if="!isSlideOnScreen(slide, idx)" class="thumbnail-image">
          <img
            v-if="slide.type != 'video'"
            :style="imageStyle(idx)"
            :src="imageSource(slide)"
            :class="{
              'group-selected-outline': groupedSlideIds.includes(slide.id)
            }"
          />
          <img
            v-if="slide.type == 'video'"
            :style="imageStyle(idx)"
            :src="videoThumbnailSource(slide)"
            :class="{
              'group-selected-outline': groupedSlideIds.includes(slide.id)
            }"
          />
        </div>
      </div>

      <div :style="slideRailStyle" class="slide_rail" />

      <!-- Overlay starts -->
      <div
        v-for="(overlay, idx) in show.data.overlayArray"
        :key="'overlay_start' + overlay.id"
        v-dragged="e => onOverlayStartDragged(e, overlay)"
        :style="overlayStartStyle(overlay, idx)"
        class="overlay_starts"
      />

      <!-- Overlays -->
      <div
        v-for="(overlay, idx) in show.data.overlayArray"
        :id="'overlay__' + overlay.id + overlay.layer"
        :key="overlay.id + overlay.layer"
        v-dragged="e => onOverlayDragged(e, overlay)"
        :style="overlayStyle(overlay, idx)"
        :class="{
          'group-selected-outline': groupedOverlayIds.includes(overlay.id)
        }"
        class="overlays unselectable"
      >
        <span>{{ overlay.text }}</span>
        <b-tooltip
          v-if="false"
          :target="'overlay__' + overlay.id + overlay.layer"
        >
          {{ overlay.id }}
        </b-tooltip>
      </div>

      <!-- Overlay ends -->
      <div
        v-for="(overlay, idx) in show.data.overlayArray"
        :key="'overlay_end' + overlay.id"
        v-dragged="e => onOverlayEndDragged(e, overlay)"
        :style="overlayEndStyle(overlay, idx)"
        class="overlay_ends"
      />

      <!-- Overlay rails -->
      <div
        v-for="(overlay, idx) in show.data.overlayArray"
        :key="'overlay_rail' + overlay.id"
        v-dragged="e => timelineDragged(e)"
        :style="overlayRailStyle(overlay, idx)"
        class="overlay_rails"
      />

      <!-- Overlay stroke rails -->
      <div
        v-for="(overlay, idx) in show.data.overlayArray"
        :key="'overlay_stroke_rail' + overlay.id"
        :style="overlayRailStyle(overlay, idx)"
        class="overlay_stroke_rails"
      />

      <TimelineCanvas
        v-if="$store.state.waveform.waveformDataLoaded"
        :key="'timeline-canvas' + timelineWidth + timelineHeight"
        v-dragged="e => timelineDragged(e)"
        :style="canvasStyle"
        :width="timelineWidth"
        :height="timelineHeight"
        class="timeline-canvas"
      />

      <!-- Timeline labels -->

      <div
        v-for="(mark, idx) in timelineMarkers"
        :key="'mark_' + idx"
        :style="markerStyle(mark)"
        class="markers unselectable"
      >
        <span v-if="mark <= timelineMarkerSwitchSideTime" class="time-left">
          {{ mark | toHHMMSS }}
        </span>
        <span v-if="mark > timelineMarkerSwitchSideTime" class="time-right">
          {{ mark | toHHMMSS }}
        </span>
        <span class="time-mark" />
      </div>

      <div
        v-if="!displayStaticPlayhead"
        id="playHead"
        ref="playHead"
        :style="playHeadStyle"
      />
    </div>

    <div
      v-if="displayStaticPlayhead"
      id="staticPlayHead"
      ref="staticPlayHead"
      :style="staticPlayHeadStyle"
    />

    <div class="timeline-overview">
      <TimelineOverviewCanvas
        v-if="$store.state.waveform.waveformDataLoaded"
        :key="'timeline-overview-canvas' + timelineHeight"
        v-dragged="e => timelineOverviewDragged(e)"
        :current-time="currentTime"
        :timeline-window-in-percent="timelineWindowInPercent"
        :timeline-window-out-percent="timelineWindowOutPercent"
        :width="componentWidth"
        :height="timelineOverviewHeight"
        :style="timelineOverlayStyle"
        class="timeline-overview-canvas"
      />
      <!-- Timeline overlay labels -->

      <div
        v-for="(mark, idx) in timelineOverviewMarkers"
        :key="'overview_mark_' + idx"
        :style="overviewMarkerStyle(mark)"
        class="overview_markers unselectable"
      >
        <span v-if="mark <= timelineMarkerSwitchSideTime" class="time-left">
          {{ mark | toHHMMSS }}
        </span>
        <span v-if="mark > timelineMarkerSwitchSideTime" class="time-right">
          {{ mark | toHHMMSS }}
        </span>
        <span class="time-mark" />
      </div>
    </div>

    <QuickKeys
      @playToggle="player_ref.playToggle"
      @timelineThumbLarger="slideHeight += 10"
      @timelineThumbSmaller="slideHeight -= 10"
      @zoomIn="
        timelineWidth = timelineWidth * 1.3;
        updateTimelinesToCurrentTimeAndCenter();
      "
      @zoomOut="
        timelineWidth = timelineWidth * 0.7;
        updateTimelinesToCurrentTimeAndCenter();
      "
      @scrubRewind="scrubFromQuickKey(currentTime - 2.5)"
      @scrubForward="scrubFromQuickKey(currentTime + 1.5)"
      @nextImage="nextImage"
      @previousImage="previousImage"
      @focusToggle="toggleFocus"
    />

    <b-row v-if="false" class="mt-4">
      <b-col>
        <vue-slider
          v-model="slideHeight"
          :min="10"
          :max="400"
          :interval="1"
          :lazy="false"
          :height="8"
          :dot-size="20"
          class="mr-3 mb-4"
          :adsorb="true"
        />
        <p class="sliderExplainer">slideHeight</p>
      </b-col>
      <b-col>
        <vue-slider
          v-model="overlaysRowHeight"
          :min="10"
          :max="100"
          :interval="1"
          :lazy="false"
          :height="8"
          :dot-size="20"
          class="mr-3 mb-4"
          :adsorb="true"
        />
        <p class="sliderExplainer">overlaysRowHeight</p>
      </b-col>
      <b-col>
        <vue-slider
          v-model="timelineWidth"
          :min="componentWidth"
          :max="32000"
          :interval="1"
          :lazy="false"
          :height="8"
          :dot-size="20"
          class="mr-3 mb-4"
          :adsorb="true"
        />
        <p class="sliderExplainer">timelineWidth</p>
      </b-col>
      <b-col>
        <vue-slider
          v-model="timelineHeight"
          :min="40"
          :max="400"
          :interval="10"
          :lazy="false"
          :height="8"
          :dot-size="20"
          class="mr-3 mb-4"
          :adsorb="true"
        />
        <p class="sliderExplainer">timelineHeight</p>
      </b-col>
      <b-col>
        <vue-slider
          v-model="timelineOverviewHeight"
          :min="40"
          :max="200"
          :interval="10"
          :lazy="false"
          :height="8"
          :dot-size="20"
          class="mr-3 mb-4"
          :adsorb="true"
        />
        <p class="sliderExplainer">timelineOverviewHeight</p>
      </b-col>
    </b-row>
    <b-row v-if="false" class="mt-4">
      <b-col>
        <b-form-checkbox
          id="checkbox-1"
          v-model="pushMode"
          name="checkbox-1"
          :value="true"
          :unchecked-value="false"
        >
          Enable Push Mode
        </b-form-checkbox>
      </b-col>
      <b-col>
        <b-form-checkbox
          id="checkbox-fluid-timeline"
          v-model="fluidTimeline"
          name="checkbox-fluid-timeline"
          :value="true"
          :unchecked-value="false"
        >
          Fluid timeline
        </b-form-checkbox>
      </b-col>
      <b-col>
        <b-form-checkbox
          id="checkbox-editable-overlays"
          v-model="editableOverlays"
          name="checkbox-editable-overlays"
          :value="true"
          :unchecked-value="false"
        >
          Editable overlays
        </b-form-checkbox>
      </b-col>
      <b-col>
        <b-form-checkbox
          id="checkbox-absolute-player"
          v-model="timelineFocusLayout"
          name="checkbox-absolute-player"
          :value="true"
          :unchecked-value="false"
        >
          Absolute Player
        </b-form-checkbox>
      </b-col>
      <b-col>
        <span v-show="$store.state.show.pendingSave">
          <span class="ml-2 small text-muted">Saving ...</span>
          <b-spinner small variant="light" type="grow" label="Saving" />
        </span>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import isEqual from "lodash/isEqual";
import isUndefined from "lodash/isUndefined";
import throttle from "lodash/throttle";
import QuickKeys from "@/components/show/QuickKeys";
import TimelineCanvas from "@/components/show/timeline/TimelineCanvas";
import TimelineOverviewCanvas from "@/components/show/timeline/TimelineOverviewCanvas";
import dragged from "@/directives/v-dragged/directive";
import { MixinTimeline } from "./MixinTimeline";
import { MixinOverlays } from "./MixinOverlays";
import { MixinOverviewTimeline } from "./MixinOverviewTimeline";
export default {
  name: "Timeline",
  components: {
    QuickKeys,
    TimelineCanvas,
    TimelineOverviewCanvas
  },
  directives: {
    dragged: dragged
  },
  mixins: [MixinTimeline, MixinOverlays, MixinOverviewTimeline],
  props: {
    player_ref: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      watchInterval: null,
      currentTime: 0,
      dragStartX: 0,
      dragStartBox: {},
      timelineLeft: 0,
      timelineDragging: false,
      timelineOverlayDragging: false,
      overlayHandleWidth: 8,
      playHeadLeft: null,
      componentWidth: 100,
      componentLeft: 0,
      componentRight: 100,
      groupedItemIds: [],
      groupedSlideIds: [],
      lastSlideIdSelected: null,
      lastSlideEventType: null,
      groupedOverlayIds: [],
      lastOverlayIdSelected: null,
      lastOverlayEventType: null,
      staticPlayheadOffsetPercent: 0.25,
      throttledOnResize: () => {},
      timelineWidthSliderHeight: 10,
      timelineTopPx: 24,
      timelineBottomPx: 24
    };
  },
  computed: {
    pushMode: {
      get() {
        return this.show.data.displaySettings.timelinePushMode || false;
      },
      set(value) {
        console.log("pushMode", value);
        this.$store.dispatch("show/updatePushMode", value);
      }
    },
    fluidTimeline: {
      get() {
        return this.$store.state.fluidTimeline || false;
      },
      set(value) {
        console.log("fluidTimeline", value);
        this.$store.commit("setFluidTimeline", value);
        this.onResize();
      }
    },
    editableOverlays: {
      get() {
        return this.$store.state.show.editableOverlays || false;
      },
      set(value) {
        console.log("editableOverlays", value);
        this.$store.commit("show/setEditableOverlays", value, { root: true });
      }
    },
    timelineFocusLayout: {
      get() {
        return this.$store.state.show.timelineFocusLayout || false;
      },
      set(value) {
        console.log("timelineFocusLayout", value);
        this.$store.commit("show/settimelineFocusLayout", value, {
          root: true
        });
      }
    },
    show() {
      return this.$store.state.show.currentShow;
    },
    playHeadStyle() {
      var left =
        (this.currentTime / this.show.data.audio.duration) * this.timelineWidth;
      if (left > this.timelineWidth - 1) {
        left = this.timelineWidth - 1;
      }
      return {
        height:
          this.slidesTopPx + this.slideHeight + this.timelineHeight + "px",
        left: left + "px"
      };
    },
    staticPlayHeadStyle() {
      return {
        "margin-top":
          -(this.slidesTopPx + this.slideHeight + this.timelineHeight) + "px",
        height:
          this.slidesTopPx + this.slideHeight + this.timelineHeight + "px",
        left: this.componentWidth * this.staticPlayheadOffsetPercent + "px"
      };
    },
    displayStaticPlayhead() {
      if (!this.timelineOverlayDragging) {
        return false;
      }
      if (
        this.timelineOverlayDragging &&
        this.timelineLeft != 0 &&
        this.timelineLeft != -(this.timelineWidth - this.componentWidth)
      ) {
        return true;
      } else {
        return false;
      }
    },
    timelineStyle() {
      return {
        left: this.timelineLeft + "px",
        top: this.timelineTopPx + "px",
        height:
          this.timelineTopPx +
          this.slidesTopPx +
          this.slideHeight +
          this.timelineHeight +
          "px"
      };
    },
    timelineOverlayStyle() {
      return {
        height: this.timelineOverviewHeight + 2 + "px"
      };
    },
    slideRailStyle() {
      return {
        top: this.slidesTopPx + "px",
        height: this.slideHeight + "px",
        width: this.timelineWidth + "px"
      };
    },
    canvasStyle() {
      return {
        top: this.slidesTopPx + this.slideHeight + "px"
      };
    },
    slidesTopPx() {
      return this.show.data.overlayArray.length * this.overlaysRowHeight;
    },
    timelineMarkerSwitchSideTime() {
      return this.show.data.audio.duration * 0.95;
    },

    overlayCount() {
      return this.show.data.overlayArray.length;
    },
    slideHeight: {
      get() {
        return this.$store.state.timelineLayout.slideHeight;
      },
      set(value) {
        var update = {
          propName: "slideHeight",
          value: value
        };
        this.$store.dispatch("timelineLayout/setPropValue", update, {
          root: true
        });
      }
    },
    overlaysRowHeight: {
      get() {
        return this.$store.state.timelineLayout.overlaysRowHeight;
      },
      set(value) {
        var update = {
          propName: "overlaysRowHeight",
          value: value
        };
        this.$store.dispatch("timelineLayout/setPropValue", update, {
          root: true
        });
      }
    },
    timelineWidth: {
      get() {
        return this.$store.state.timelineLayout.timelineWidth;
      },
      set(value) {
        var update = {
          propName: "timelineWidth",
          value: value
        };
        this.$store.dispatch("timelineLayout/setPropValue", update, {
          root: true
        });
      }
    },
    timelineHeight: {
      get() {
        return this.$store.state.timelineLayout.timelineHeight;
      },
      set(value) {
        var update = {
          propName: "timelineHeight",
          value: value
        };
        this.$store.dispatch("timelineLayout/setPropValue", update, {
          root: true
        });
      }
    },
    timelineOverviewHeight: {
      get() {
        return this.$store.state.timelineLayout.timelineOverviewHeight;
      },
      set(value) {
        var update = {
          propName: "timelineOverviewHeight",
          value: value
        };
        this.$store.dispatch("timelineLayout/setPropValue", update, {
          root: true
        });
      }
    },
    timelineWidthSliderStyle() {
      return { height: this.timelineWidthSliderHeight + "px" };
    }
  },
  watch: {
    timelineWidth() {
      this.updateTimelinesToCurrentTimeAndCenter();
    }
  },
  mounted() {
    // Setup the audio interval.
    if (this.watchInterval) {
      clearInterval(this.watchInterval);
    }
    var interval_ms = 25;
    this.watchInterval = setInterval(this.watchClock, interval_ms);

    this.$store.dispatch("waveform/loadWaveformDataIfNeeded");

    this.throttledOnResize = throttle(this.onResize, 50);

    window.addEventListener("resize", this.throttledOnResize, false);
    this.onResize();
  },
  beforeDestory() {
    window.removeEventListener("resize", this.throttledOnResize, false);
  },
  methods: {
    checkToFitHeight(newHeight) {
      //console.log("checkToFitHeight", newHeight);
      this.$store.dispatch(
        "timelineLayout/setAllTimelineValuesFromAvailableHeight",
        newHeight
      );
      /*
      var minTimelineHeight = 60;
      var maxTimelineHeight = 210;
      var minTimelineOverviewHeight = 40;
      var maxTimelineOverviewHeight = 60;

      var totalTimelineHeight =
        this.timelineTopPx +
        this.slidesTopPx +
        this.slideHeight +
        this.timelineOverviewHeight +
        this.timelineHeight +
        this.timelineBottomPx;

      var nonTimelineCanvasHeight = totalTimelineHeight - this.timelineHeight;
      var nonTimelineOverviewHeight =
        totalTimelineHeight - this.timelineOverviewHeight;
      if (totalTimelineHeight > newHeight) {
        // Reduce the size of the timelineHeight;
        // Expand only the timeline and the timelineOverview.

        if (newHeight - nonTimelineCanvasHeight > minTimelineHeight) {
          this.timelineHeight = newHeight - nonTimelineCanvasHeight;
        } else {
          // Reduce the timelineOverviewHeight instead.
          if (
            newHeight - nonTimelineOverviewHeight >
            minTimelineOverviewHeight
          ) {
            this.timelineOverviewHeight = newHeight - nonTimelineOverviewHeight;
          } else {
            // Need to reduce something else or stop getting smaller.
          }
        }
      } else {
        // Attempt to max out the timeline canvases.
        if (newHeight - nonTimelineCanvasHeight < maxTimelineHeight) {
          this.timelineHeight = newHeight - nonTimelineCanvasHeight;
        } else {
          if (
            newHeight - nonTimelineOverviewHeight <
            maxTimelineOverviewHeight
          ) {
            this.timelineOverviewHeight = newHeight - nonTimelineOverviewHeight;
          }
        }
      }
      */
    },
    toggleFocus() {
      this.timelineFocusLayout = !this.timelineFocusLayout;
    },
    scrubFromQuickKey(seconds) {
      if (this.player_ref.audio_ref.paused) {
        this.player_ref.gotoAndPause(seconds);
        this.checkForTimelineScroll();
      } else {
        this.player_ref.gotoAndPlay(seconds);
      }
    },
    nextImage() {
      // TODO: This should jump to the exact inpoint,
      // instead of using the player's version of prev/next.
      // Exact inpoint is more useful when editing.
      this.player_ref.nextImage();
      this.checkForTimelineScroll();
    },
    previousImage() {
      // TODO: This should jump to the exact inpoint,
      // instead of using the player's version of prev/next.
      // Exact inpoint is more useful when editing.
      this.player_ref.previousImage();
      this.checkForTimelineScroll();
    },
    setClickedTimelineItem(activeItem) {
      if ((!isEqual(this.$store.state.show.activeItem), activeItem)) {
        this.$store.commit("show/setClickedTimelineItem", activeItem);
      }
    },
    checkForTimelineScroll() {
      // Check to see if the timeline needs scrolling after quickkey scrub/jump.
      setTimeout(this.updateTimelinesToCurrentTime, 100);
      setTimeout(this.updateTimelinesToCurrentTime, 500);
    },
    onResize() {
      this.$nextTick(function() {
        var boundingRect = this.$el.getBoundingClientRect();
        this.componentWidth = boundingRect.width;
        this.componentLeft = boundingRect.left;
        this.componentRight = boundingRect.right;
      });
    },

    setTimelineLeft(newLeft) {
      if (newLeft > 0) {
        newLeft = 0;
      }
      if (newLeft < -(this.timelineWidth - this.componentWidth)) {
        newLeft = -(this.timelineWidth - this.componentWidth);
      }
      this.timelineLeft = newLeft;
    },
    imageSource(image) {
      var image_size = "400";
      var image_root = this.$store.state.imageEndpoint;
      return `${image_root}/${image.source_id}/${image_size}/${image.file}`;
    },
    videoThumbnailSource(image) {
      var videoEndpoint = this.$store.state.videoEndpoint;
      return `${videoEndpoint}/${image.source_id}/${image.file}_00001.jpg`;
    },
    watchClock() {
      if (this.player_ref) {
        var audio_ref = this.player_ref.audio_ref;
        this.currentTime = audio_ref.currentTime;

        if (!audio_ref.paused) {
          this.updateTimelinesToCurrentTime();
        }
      }

      //console.log("watchClock", this.currentTime);
    },
    updateTimelinesToCurrentTimeAndCenter() {
      var offsetPixels = this.componentWidth * this.staticPlayheadOffsetPercent;
      var newLeft =
        offsetPixels -
        (this.currentTime / this.show.data.audio.duration) * this.timelineWidth;
      this.setTimelineLeft(newLeft);
    },
    updateTimelinesToCurrentTime() {
      if (
        isUndefined(this.$refs.playHead) ||
        isUndefined(this.$refs.timelineMain)
      ) {
        return;
      }
      var playHeadLeft = this.$refs.playHead.getBoundingClientRect().left;
      var timelineRect = this.$refs.timelineMain.getBoundingClientRect();
      if (
        playHeadLeft < timelineRect.left ||
        playHeadLeft > timelineRect.right
      ) {
        var offsetPixels = 60; // Amount of pixels overlap when updating.
        var newLeft =
          offsetPixels -
          (this.currentTime / this.show.data.audio.duration) *
            this.timelineWidth;
        this.setTimelineLeft(newLeft);
      }
    },
    slideWidthPx(idx) {
      var slideArray = this.show.data.slideArray || [];
      var duration_ms = this.show.data.audio.duration * 1000;
      if (idx < slideArray.length - 1) {
        return (
          ((slideArray[idx + 1].timing - slideArray[idx].timing) /
            duration_ms) *
          this.timelineWidth
        );
      } else {
        return (
          ((duration_ms - slideArray[idx].timing) / duration_ms) *
          this.timelineWidth
        );
      }
    },

    markerStyle(mark) {
      //console.log(mark);
      return {
        top: this.slidesTopPx + this.slideHeight + "px",
        left:
          ((mark * 1000) / (this.show.data.audio.duration * 1000)) *
            this.timelineWidth +
          "px"
      };
    },
    overviewMarkerStyle(mark) {
      //console.log(mark);
      return {
        bottom: 0 + "px",
        left:
          ((mark * 1000) / (this.show.data.audio.duration * 1000)) *
            this.componentWidth +
          "px"
      };
    }
  }
};
</script>

<style lang="scss" scoped>
#playHead {
  position: absolute;
  border-left: solid 1px red;
  width: 1px;
  top: 0px;
  height: 400px;
  z-index: 1001;
  pointer-events: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  user-select: none;
}
#staticPlayHead {
  position: relative;
  border-left: solid 1px red;
  width: 1px;
  top: 0px;
  height: 400px;
  z-index: 1002;
  pointer-events: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  user-select: none;
}

.timeline-canvas {
  position: relative;
  top: 0px;
  left: 0px;
}

.timeline-main {
  height: 100%;
  width: 100%;
  overflow: hidden;
}
.timeline {
  position: relative;
  margin: 0;
  width: 100%;
}

.timeline-overview {
  position: relative;
  width: 100%;
}
.timeline-overview-canvas {
  border-top: solid 1px black;
  border-bottom: solid 1px black;
}

.slides {
  position: absolute;
  //top: 50px;
  //height: 50px;
  border-top: solid 1px black;
  border-bottom: solid 1px black;
  border-left: solid 1px black;
  background-color: #ffc2e2;
  &:hover {
    //background-color: limegreen;
    background-color: rgba(0, 0, 0, 0.2);
  }

  .thumbnail-image {
    /* Here is the important code*/
    position: relative;

    img {
      object-fit: contain;
      pointer-events: none;
      -moz-user-select: none;
      -webkit-user-select: none;
      user-select: none;
    }
  }

  z-index: 1000;
}
.unselectable {
  -moz-user-select: none;
  -webkit-user-select: none;
  user-select: none;
}
.slide_rail {
  position: absolute;
  top: 0px;
  height: 50px;
  border-top: solid 1px black;
  //background-color: #ffc2e2;
  z-index: 999;
}
.overlay_rails {
  position: absolute;
  top: 0px;
  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
  z-index: 999;
}
.overlay_stroke_rails {
  position: absolute;
  top: 0px;
  border-top: solid 1px black;
  pointer-events: none;
  z-index: 1001;
}

.overlays {
  position: absolute;
  top: 0px;
  border-top: solid 1px black;
  border-left: solid 1px black;
  background-color: #ffc2e2;
  overflow: hidden;
  padding: 2px;
  font-family: sans-serif;
  font-size: 14px;
  &:hover {
    background-color: rgba(0, 0, 0, 0.2);
    overflow: visible;
  }
  z-index: 1000;
}
.overlay_starts {
  position: absolute;
  top: 0px;
  border-left: solid 1px rgba(0, 0, 0, 0.2);
  background-color: rgba(0, 0, 0, 0.8);
  &:hover {
    background-color: rgba(0, 0, 0, 0.6);
  }
  z-index: 1001;
}
.overlay_ends {
  position: absolute;
  top: 0px;
  border-left: solid 1px rgba(0, 0, 0, 0.2);
  background-color: rgba(0, 0, 0, 0.8);
  &:hover {
    background-color: rgba(0, 0, 0, 0.6);
  }
  z-index: 1000;
}
.markers {
  border-left: 1px solid black;
  position: absolute;
  width: 10px;
  height: 10px;
  color: #666;
  font-size: 12px;
  font-family: sans-serif;
  z-index: 1000;
  .time-mark {
    position: absolute;
    left: -1px;
    top: 0;
    padding-left: 4px;
    height: 8px;
    width: 1px;
    border-left: 1px solid black;
  }
  .time-left {
    position: absolute;
    text-align: left;
    left: 0px;
    top: -1px;
    //background-color: pink;
    width: 20px;
    padding-left: 4px;
  }
  .time-right {
    position: absolute;
    text-align: right;
    right: 10px;
    //background-color: pink;
    width: 50px;
    top: -1px;
    padding-right: 4px;
  }
}

.overview_markers {
  border-left: 1px solid black;
  position: absolute;
  width: 10px;
  height: 10px;
  color: #666;
  font-size: 0.74em;
  font-family: "Ubuntu", sans-serif;
  z-index: 1000;
  .time-mark {
    position: absolute;
    left: -1px;
    top: 0;
    padding-left: 4px;
    height: 8px;
    width: 1px;
    border-left: 1px solid black;
  }
  .time-left {
    position: absolute;
    text-align: left;
    left: 0px;
    bottom: -2px;
    //background-color: pink;
    width: 20px;
    padding-left: 4px;
  }
  .time-right {
    position: absolute;
    text-align: right;
    right: 10px;
    //background-color: pink;
    width: 50px;
    bottom: -2px;
    padding-right: 4px;
  }
}

.sliderExplainer {
  margin-top: -20px;
  font-size: 12px;
}

.slides-selected {
  //background-color: limegreen;
}

.group-selected-outline {
  outline: 4px solid limegreen;
  outline-offset: -4px;
}
.timeline-width-slider {
  position: absolute;
  top: 2px;
  right: 20px;
  width: 200px;
}
</style>
